"use client";

import React, { useEffect, useRef, useState } from 'react';
import styles from './ServiceStateCard.module.scss';
import { MyProfileStateCardData } from '@components/molecules/MyProfileStateCard/MyProfileStateCard.data';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import assetLoader from '@utils/internalImageLoder';
import { trackEventWithCommonProps, EventType } from '@utils/eventTracker';
import { useGlobalLoader } from '@hooks/useGlobalLoader';
import { PAGE_TYPES } from '@constants/pageTypes';
import Link from 'next/link';
import { getGarageSlotsData } from '@services/garageService';
import { ServiceDataTransform, slotsTransformer } from './ServiceDataTransform';
import Text from '../Text';
import { formatDateTime } from '@helpers/dateFormatter';
import { SERVICE_STATE_CONFIG } from '@components/organisms/MyProfileStates/MyProfileStates.data';
import { useEqualHeights } from '@hooks/useEqualHeights';
import { inApp } from '@utils/platformDetection.client';
import { useCity } from '@context/CityContext';
import { getSelectedCityClient } from '@helpers/client/getSelectedCity';
import { USER_SELECTED_CITY_COOKIE_NAME } from '@constants/cityCookies';
import { defaultCity } from '@constants/cities';
import Cookies from 'js-cookie';

interface ServiceStateCardProps {
  card: MyProfileStateCardData;
  cityName?: string;
  userId?: any;
  garageServiceData?: any;
  isLoggedIn?: any;
}

const ServiceStateCard: React.FC<ServiceStateCardProps> = ({ card, cityName, garageServiceData, isLoggedIn }) => {

  const router = useRouter();
  const pathname = usePathname();
  const { showLoader, hideLoader } = useGlobalLoader();
  const { syncDetectedCity } = useCity();
  const [serviceData, setServiceData] = useState<any>([]);
  const [carServiceShow, setCarServiceShow] = useState<any>(false);
  const hasRequestedFallbackSlots = useRef(false);
  const isInApp = inApp();
  const isHomePage = pathname === "/";
  const serviceHref = isInApp
    ? `${process.env.NEXT_PUBLIC_FRONTEND_HOST}/service-centre/splash-screen`
    : `${process.env.NEXT_PUBLIC_FRONTEND_HOST}/service-centre`;
  
  const { elementRef: mainInfoRef } = useEqualHeights({ 
    sectionType: 'mainInfo',
    dependencies: [serviceData, carServiceShow],
  });
  const { elementRef: cardFooterRef } = useEqualHeights({ 
    sectionType: 'cardFooter',
    dependencies: [serviceData, carServiceShow],
  });

  useEffect(() => {
    hideLoader();
  }, [pathname, hideLoader]);

  useEffect(() => {
    const getServiceData = async () => {
        if(isLoggedIn && garageServiceData?.success){
          const transformedData = ServiceDataTransform(garageServiceData?.data);
          setServiceData(transformedData);
          setCarServiceShow(true);
        } else {
          // Effects may be intentionally replayed in development. Location is a
          // user-permission side effect, so request fallback slots only once per
          // mounted card to avoid duplicate permission-denied failures/logs.
          if (hasRequestedFallbackSlots.current) {
            return;
          }
          hasRequestedFallbackSlots.current = true;

          const garageSlotsResult = await getGarageSlotsData();
          const currentSelectedCity = getSelectedCityClient();
          const isUserSelectedCity = Cookies.get(USER_SELECTED_CITY_COOKIE_NAME) === "true";
          const currentCitySlug =
            currentSelectedCity?.slug ||
            (currentSelectedCity as any)?.city_slug ||
            "";
          const isDefaultOrUnsetCity =
            !currentCitySlug ||
            currentCitySlug === defaultCity.city_slug ||
            currentSelectedCity?.name === defaultCity.name;

          if (
            isHomePage &&
            !isInApp &&
            garageSlotsResult?.resolvedCity &&
            !isUserSelectedCity &&
            isDefaultOrUnsetCity &&
            garageSlotsResult.resolvedCity.city_slug !== currentCitySlug
          ) {
            syncDetectedCity(garageSlotsResult.resolvedCity);
            router.refresh();
          }

          if (garageSlotsResult?.success) {
            const transformedData = slotsTransformer(garageSlotsResult?.data);
            if(transformedData.length > 0){
              setServiceData({
                stage: "slot_details",
                data: transformedData
              });
              setCarServiceShow(true);
            }
          }
        }
      };
    getServiceData();
  }, [garageServiceData, isHomePage, isInApp, isLoggedIn, router, syncDetectedCity]);

  const handleClick = async (stage?: string, segmentStageName?: string, regNo?: string) => {
    showLoader('Loading...');
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: PAGE_TYPES.HOME_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_service_car",
        city_name: cityName,
        ...(stage && segmentStageName && { card_name : segmentStageName })
      },
    });

    if(stage === "upcoming_service" || stage === "ongoing_service"){
      router.push(`${process.env.NEXT_PUBLIC_FRONTEND_HOST}/service-centre/stage-redirect`)
    } else if(stage === "next_service_date" && regNo){
      router.push(`${process.env.NEXT_PUBLIC_FRONTEND_HOST}/service-centre/service-reminder?vehicle_registration_number=${regNo}`)
    } else if (isInApp){
      router.push(`${process.env.NEXT_PUBLIC_FRONTEND_HOST}/service-centre/splash-screen`)
    } 
    else {
      router.push(`${process.env.NEXT_PUBLIC_FRONTEND_HOST}/service-centre`)
    }
  }

  return (
    <>
      <div className={styles.cardContainer}>
        <div className={styles.cardContent}>
          <div className={styles.mainInfo} ref={mainInfoRef} data-section-type="mainInfo">
            <div className={styles.IconWitharrow}>
              <div className={styles.innerTitleWithIcon} >
                <Image
                  src={`${assetLoader()}${card.icon}`}
                  alt='Arrow Right'
                  fill
                  className={styles.ctaSVG}
                  priority
                />
                <Link
                  href={serviceHref}
                  onClick={() => handleClick()}
                  className={styles.ctaButton}
                >
                  <Image
                    src={`${assetLoader()}/icons/Arrow_right.svg`}
                    alt={card.ctaIconName}
                    fill
                    className={styles.ctaIcon}
                    priority
                  />
                </Link>
              </div>
              <Link href={serviceHref} onClick={() => handleClick()}>
                <h3 className={styles.title}>{card.title}</h3>
              </Link>
            </div>
            <p className={styles.subText}>
              {card.subText.map((segment, segmentIndex) => (
                <span
                  key={segmentIndex}
                  className={segment.color ? `${styles[segment.color]} ${styles.subTextColored}` : ""}
                >
                  {segment.text}
                </span>
              ))}
            </p>
          </div>

          {
            carServiceShow && !(serviceData.stage === "no_service") && (
              <div className={styles.cardFooter} ref={cardFooterRef} data-section-type="cardFooter">

                {
                  (serviceData.stage === "slot_details" && serviceData.data.length > 0) && (
                    <div className={styles.slotsContainer}>
                      {
                        serviceData.data.map((slot: any, slotIndex: any) => (
                          <div
                            key={slotIndex}
                            className={styles.slotItem}
                            onClick={() => handleClick(serviceData.stage, "No booking")}
                          >
                            <span className={styles.slotDay}>{slot.slotDate}</span>
                            <span className={styles.slotCount}>{slot.slotCount}</span>
                          </div>
                        ))
                      }
                    </div>
                  )
                }

                { isLoggedIn &&
                  SERVICE_STATE_CONFIG.map((config: any) => {
                    const isCurrentStage = serviceData.stage === config.stage;
                    const hasData = config.isMultiple ? serviceData.data?.length > 0 : serviceData.data;
                    
                    if (!isCurrentStage || !hasData) return null;

                    const renderServiceState = (item: any, index: number) => {
                      const clickHandler = config.getClickHandler(serviceData.stage, item);
                      const title = config.getTitle(item);
                      const subtitle = config.getSubtitle(item);
                      const icon = config.getIcon ? config.getIcon(item) : config.icon;
                      
                      return (
                        <div
                          className={`${styles.serviceContainer} ${config.isMultiple && serviceData.data.length > 1 ? styles.moreServiceCar : ''}`}
                          key={index}
                          onClick={() => handleClick(serviceData.stage, clickHandler.segmentStageName, clickHandler.regNo)}
                        >
                          <Image
                            src={`${assetLoader()}${icon}` || ""}
                            alt={config.altText}
                            width={48}
                            height={48}
                            className={styles.serviceIcon}
                          />
                          <div className={styles.serviceDetails}>
                            <Text className={styles.serviceRegNumber}>
                              {title}
                            </Text>
                            <Text className={config.stage === "upcoming_service" ? styles.upcomingServiceStatus : styles.serviceStatus}>
                              {config.stage === "upcoming_service" ? formatDateTime(subtitle) : subtitle}
                            </Text>
                          </div>
                        </div>
                      );
                    };

                    return (
                      <div 
                        key={config.stage}
                        className={`${config.isScrollable && serviceData.data.length > 1 ? styles.scrollableInnerContainer : styles.innerContainer}`}
                      >
                        {config.isMultiple 
                          ? serviceData.data.map((item: any, index: number) => renderServiceState(item, index))
                          : renderServiceState(serviceData.data, 0)
                        }
                      </div>
                    );
                  })
                }
              </div>
            )
          }
        </div>
      </div>
    </>
  );
};

export default ServiceStateCard;
